import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  Send,
  ArrowRight,
  Facebook,
} from "lucide-react";
import Seo from "@/components/site/Seo";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";

const REQUIRED = ["name", "phone", "message"];

export default function Contact() {
  const { settings } = useSettings();
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: null }));
  };

  const phoneClean = (settings.phone || "0753567030").replace(/\s/g, "");
  const waRaw = (settings.whatsapp || "0753567030").replace(/\s/g, "");
  const waNum = waRaw.startsWith("40") ? waRaw : `40${waRaw.replace(/^0/, "")}`;

  const buildMessage = (f) => {
    const line = (label, v) => (v ? `${label}: ${v}` : null);
    return [
      "Salut!",
      "Am o întrebare / cerere de ofertă.",
      "",
      line("Nume", f.name),
      line("Telefon", f.phone),
      line("Email", f.email),
      line("Subiect", f.subject),
      "",
      f.message ? `Mesaj:\n${f.message}` : null,
      "",
      "Aștept un răspuns.",
      "Mulțumesc!",
    ].filter(Boolean).join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    const nextErr = {};
    for (const k of REQUIRED) if (!String(form[k] || "").trim()) nextErr[k] = "Câmp obligatoriu";
    if (Object.keys(nextErr).length) {
      setErrors(nextErr);
      toast.error("Te rugăm completează câmpurile obligatorii.");
      document.querySelector(`[name="${REQUIRED.find((k) => nextErr[k])}"]`)?.focus();
      return;
    }
    window.open(`https://wa.me/${waNum}?text=${encodeURIComponent(buildMessage(form))}`, "_blank", "noopener,noreferrer");
    toast.success("Mesajul e pregătit în WhatsApp — apasă Trimite acolo.");
  };

  const cards = [
    {
      icon: Phone,
      label: "Telefon",
      value: settings.phone,
      valueSecondary: settings.phone_secondary,
      hrefSecondary: settings.phone_secondary ? `tel:${settings.phone_secondary.replace(/\s/g, "")}` : null,
      href: `tel:${phoneClean}`,
      cta: "Sună acum",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: settings.phone,
      href: `https://wa.me/${waNum}`,
      cta: "Scrie-ne",
      external: true,
      accent: "green",
    },
    {
      icon: Mail,
      label: "Email",
      value: settings.email,
      href: `mailto:${settings.email}`,
      cta: "Trimite email",
    },
    {
      icon: MapPin,
      label: "Adresă",
      value: `${settings.address_street}, ${settings.address_city}`,
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${settings.address_street}, ${settings.address_city}, ${settings.address_country}`,
      )}`,
      cta: "Vezi pe hartă",
      external: true,
    },
  ];

  const mapUrl = `https://maps.google.com/maps?q=${settings.latitude || 47.6435},${
    settings.longitude || 26.2411
  }&z=15&output=embed`;

  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-20">
      <Seo title="Contact · AutoRIO Suceava" description="Contactează AutoRIO Suceava — telefon, WhatsApp, email și adresă. Suntem aici pentru orice întrebare sau o ofertă personalizată." path="/contact" />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-12 sm:pt-40 sm:pb-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(60% 55% at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 60%)" }} />
        <div className="relative container-wide text-center max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.05] backdrop-blur-md px-4 py-1.5 border border-white/[0.1]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/80">În fiecare zi lucrătoare</span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.02] tracking-tight">
              Suntem aici{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">pentru tine</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed">
              Contactează-ne pentru orice întrebare sau pentru o ofertă personalizată.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="container-wide pt-4 pb-14 sm:pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => {
            const isGreen = c.accent === "green";
            return (
              <motion.a key={c.label} href={c.href}
                target={c.external ? "_blank" : undefined}
                rel={c.external ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group relative rounded-2xl border border-white/[0.06] bg-[#111111] p-6 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <span className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                  isGreen ? "bg-[#25D366]/12 border border-[#25D366]/30" : "bg-[#D4AF37]/12 border border-[#D4AF37]/30"
                }`}>
                  <c.icon className={`h-5 w-5 ${isGreen ? "text-[#25D366]" : "text-[#D4AF37]"}`} strokeWidth={2.2} />
                </span>
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">{c.label}</div>
                  <div className="mt-1 text-white font-bold text-[15px] leading-tight break-words">{c.value}</div>
                  {c.valueSecondary && (
                    <div className="mt-1 text-white font-bold text-[15px] leading-tight break-words">
                      {c.valueSecondary}
                    </div>
                  )}
                </div>
                <div className={`mt-5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] ${
                  isGreen ? "text-[#25D366]" : "text-[#D4AF37]"
                } group-hover:translate-x-0.5 transition-transform`}>
                  {c.cta} <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </motion.a>
            );
          })}
        </div>

        {/* SCHEDULE strip */}
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#111111] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="h-11 w-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
            <Clock className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.2} />
          </span>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">Program</div>
            <div className="mt-1 text-white/85 text-[14px] font-medium">
              {settings.schedule || "Luni – Vineri: 09:00 – 18:00 · Sâmbătă: 10:00 – 14:00 · Duminică: Închis"}
            </div>
          </div>
          <a href={settings.facebook_url || "https://www.facebook.com/DealerAutoRIO"} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-bold text-white/80 hover:text-[#D4AF37] transition-colors">
            <Facebook className="h-4 w-4" /> Urmărește-ne
          </a>
        </div>
      </section>

      {/* MAP + FORM */}
      <section className="container-wide py-8 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" ref={formRef}>
        {/* Map */}
        <div className="relative rounded-3xl overflow-hidden border border-white/[0.08] bg-[#111111] min-h-[420px] lg:min-h-[560px]">
          <iframe
            title="Locația AutoRIO"
            src={mapUrl}
            className="absolute inset-0 w-full h-full grayscale-[0.35] contrast-125"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0a0a0a]/90 to-transparent" />
          <div className="absolute top-5 left-5 right-5 pointer-events-none">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0a0a0a]/85 backdrop-blur border border-white/[0.1] px-4 py-2">
              <MapPin className="h-4 w-4 text-[#D4AF37]" />
              <span className="text-[12px] font-bold text-white">
                {settings.address_street}, {settings.address_city}
              </span>
            </div>
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${settings.address_street}, ${settings.address_city}`,
            )}`}
            target="_blank" rel="noopener noreferrer"
            className="absolute bottom-5 right-5 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-5 py-2.5 font-bold text-[11px] uppercase tracking-[0.16em] transition-colors shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)]">
            Deschide în Maps <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* Form */}
        <form onSubmit={submit} noValidate className="rounded-3xl border border-white/[0.06] bg-[#111111] p-6 sm:p-8 space-y-4">
          <div>
            <div className="eyebrow">Formular contact</div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Scrie-ne un{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">mesaj</span>
            </h2>
            <p className="mt-2 text-white/50 text-sm">
              Trimiterea se face direct prin WhatsApp, cu mesaj pre-completat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CField label="Nume" name="name" required value={form.name} onChange={set("name")} error={errors.name} placeholder="Prenume și nume" autoComplete="name" />
            <CField label="Telefon" name="phone" required type="tel" value={form.phone} onChange={set("phone")} error={errors.phone} placeholder="07XX XXX XXX" autoComplete="tel" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CField label="Email (opțional)" name="email" type="email" value={form.email} onChange={set("email")} placeholder="nume@exemplu.ro" autoComplete="email" />
            <CField label="Subiect (opțional)" name="subject" value={form.subject} onChange={set("subject")} placeholder="Ex: Ofertă BMW Seria 3" />
          </div>
          <div>
            <CLabel error={errors.message}>
              Mesaj<span className="text-[#D4AF37] ml-0.5">*</span>
            </CLabel>
            <textarea
              name="message"
              value={form.message}
              onChange={set("message")}
              rows={5}
              placeholder="Cu ce te putem ajuta?"
              className={`bg-[#0a0a0a] border rounded-lg px-4 py-3 w-full text-white placeholder-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors resize-none ${
                errors.message ? "border-red-500/60" : "border-white/[0.08]"
              }`}
            />
            {errors.message && (
              <div className="mt-1.5 text-[12px] font-semibold text-red-400">{errors.message}</div>
            )}
          </div>

          <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-14 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]">
            <Send className="h-4 w-4" /> Trimite prin WhatsApp
          </button>
          <p className="text-center text-[11px] text-white/45">Nu trimitem cereri pe email. Comunicăm direct prin WhatsApp.</p>
        </form>
      </section>

      {/* CTA */}
      <section className="container-wide pt-16">
        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#0e0d0b] to-[#0a0a0a] px-6 py-14 sm:px-12 sm:py-20 text-center">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <div className="eyebrow">Interesat?</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
              Dorești o{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">ofertă</span>?
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href={`tel:${phoneClean}`} className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]">
                <Phone className="h-4 w-4" /> Sună acum
              </a>
              <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const cInp = "bg-[#0a0a0a] border rounded-lg h-12 px-4 w-full text-white placeholder-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors";
function CLabel({ children, error }) {
  return (
    <label className={`block text-[11px] font-bold uppercase tracking-[0.16em] mb-2 ${error ? "text-red-400" : "text-white/60"}`}>
      {children}
    </label>
  );
}
function CField({ label, name, required, error, ...rest }) {
  return (
    <div>
      <CLabel error={error}>
        {label}
        {required && <span className="text-[#D4AF37] ml-0.5">*</span>}
      </CLabel>
      <input name={name} className={`${cInp} ${error ? "border-red-500/60" : "border-white/[0.08]"}`} {...rest} />
      {error && <div className="mt-1.5 text-[12px] font-semibold text-red-400">{error}</div>}
    </div>
  );
}
